import { ElementSource } from "chq";
import { ELEMENT_NAMES } from "components/stories/video-studio/utils/constants";
import syncMediaElements from "components/stories/video-studio/utils/creatomate/syncMediaElements";
import deleteElement from "components/stories/video-studio/utils/creatomate/deleteElement";
import { ImplementorTemplateParams } from "components/stories/video-studio/utils/creatomate/setCustomTemplate";
import findElementByName from "../../utils/findElementByName";
import calculateVideoDuration from "../../utils/calculateVideoDuration";

const IrcDefaultVideoTemplateWithOutro = async ({
  preview,
  mediaElements,
  defaultVideoProperties,
  story
}: ImplementorTemplateParams): Promise<void> => {
  const json = preview.getSource();
  const elements = json?.elements;

  const filterElementsByName = (
    elements: ElementSource[] | undefined,
    name: typeof ELEMENT_NAMES[keyof typeof ELEMENT_NAMES]
  ): ElementSource[] => {
    const lowerCaseName = name.toLowerCase();
    return elements?.filter(element => element.name?.toLowerCase() === lowerCaseName) || [];
  };

  const outroHeaderElement = findElementByName(elements, ELEMENT_NAMES.OUTRO_HEADER);
  const outroHashtagsElement = findElementByName(elements, ELEMENT_NAMES.OUTRO_HASHTAGS);
  const outrosElements = filterElementsByName(elements, ELEMENT_NAMES.OUTRO_TEXT);
  const storyVideoElement = findElementByName(elements, ELEMENT_NAMES.STORY);
  const titleElement = findElementByName(elements, ELEMENT_NAMES.TITLE);
  const departmentElement = findElementByName(elements, ELEMENT_NAMES.DEPARTMENT);
  const locationElement = findElementByName(elements, ELEMENT_NAMES.LOCATION);
  const nameElement = findElementByName(elements, ELEMENT_NAMES.NAME);
  const largeOverlayElement = findElementByName(elements, ELEMENT_NAMES.LARGE_OVERLAY);
  const smallOverlayElement = findElementByName(elements, ELEMENT_NAMES.SMALL_OVERLAY);

  const firstOutroElement: ElementSource | undefined = outrosElements.sort(
    (firstElement: ElementSource, secondElement: ElementSource) => (
      firstElement.time - secondElement.time
    )
  )[0];

  const ELEMENTS_START_TIME = nameElement?.time ?? 0;
  const ELEMENTS_END_TIME = (ELEMENTS_START_TIME + nameElement?.duration);
  const OUTRO_DURATION = (outroHeaderElement?.duration + outroHashtagsElement?.duration);
  const IRC_OUTRO_WAITING_TIME = firstOutroElement?.time - outroHeaderElement?.time;
  const OUTRO_HEADER = outroHeaderElement?.duration;

  await syncMediaElements(preview, mediaElements, {
    ...defaultVideoProperties,
    track: storyVideoElement?.track,
    x: storyVideoElement?.x,
    width: storyVideoElement?.width,
    height: storyVideoElement?.height,
    border_radius: storyVideoElement?.border_radius
  }, 0);
  await deleteElement(preview, storyVideoElement?.id);
  let videoDuration = calculateVideoDuration(mediaElements);


  let modifications: { [x: string]: string | number } = {
    [`${nameElement?.id}.text`]: story?.creator?.name || "",
    [`${titleElement?.id}.text`]: story?.creator?.title || "",
    [`${departmentElement?.id}.text`]: story?.creator?.departments?.[0]?.name || "",
    [`${locationElement?.id}.text`]: story?.creator?.location?.name || "",
    [`${outroHeaderElement?.id}.time`]: videoDuration,
    [`${outroHashtagsElement?.id}.time`]: videoDuration + OUTRO_HEADER,
    duration: videoDuration + OUTRO_DURATION
  };

  if (videoDuration < ELEMENTS_END_TIME) {
    // We need to sustract the time that it will take the name to appear
    const newDuration = videoDuration - ELEMENTS_START_TIME;
    if (newDuration > 0) {
      modifications = {
        ...modifications,
        [`${titleElement?.id}.duration`]: newDuration,
        [`${nameElement?.id}.duration`]: newDuration,
        [`${departmentElement?.id}.duration`]: newDuration,
        [`${locationElement?.id}.duration`]: newDuration,
        [`${smallOverlayElement?.id}.duration`]: newDuration,
        [`${largeOverlayElement?.id}.duration`]: newDuration
      };
    }
  }

  outrosElements.forEach((outro: Record<string, any>) => {
    modifications = {
      ...modifications,
      [`${outro.id}.time`]: videoDuration + IRC_OUTRO_WAITING_TIME
    };
    videoDuration += outro.duration;
  });
  await preview.applyModifications(modifications);
};

export default IrcDefaultVideoTemplateWithOutro;
